import { useGetFormattedDate } from '@app/src/components/DateDisplay'
import EmptyState from '@app/src/components/EmptyState'
import { EmptyStateVariant } from '@app/src/components/EmptyState/EmptyState'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import { getRiskColor, getRiskStatusShortKey, getRiskStrongerColor } from '@app/src/utils/riskStatusHelpers'
import { DoNotDisturbAltOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material'
import type { EChartsOption } from 'echarts'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { ChartItem } from './TopicalRiskProviderChart.logic'
import { prepareChartData } from './TopicalRiskProviderChart.logic'

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer])

interface TopicalRiskProviderChartProps {
  items: ChartItem[]
}

const TopicalRiskProviderChart = ({ items }: TopicalRiskProviderChartProps) => {
  const { formatMessage } = useIntl()
  const { formatValueToDate } = useGetFormattedDate()
  const theme = useTheme()
  const { palette, typography } = theme

  const { chartData, graphTicks } = useMemo(() => prepareChartData(items), [items])

  const timestampToItemMap = useMemo(() => new Map(chartData.map(item => [item.timestamp, item])), [chartData])

  const option: EChartsOption = useMemo(
    () =>
      !chartData.length
        ? {}
        : {
            grid: {
              left: 40,
              right: 40,
              top: 40,
              bottom: 40,
            },
            tooltip: {
              trigger: 'item',
              formatter: params => {
                const callbackData = Array.isArray(params) ? params[0] : params
                const data = callbackData.data as { value: [number, number] }
                const [timestamp] = data.value
                const item = timestampToItemMap.get(timestamp)
                if (!item) return ''
                return `${formatValueToDate({ value: new Date(item.timestamp), withTime: true })}<br/>
                    <strong>${formatMessage({ id: getRiskStatusShortKey(item.riskStatus) })}</strong>`
              },
            },
            visualMap: {
              show: false,
              dimension: 1,
              min: 0,
              max: 3,
              inRange: {
                color: [
                  getRiskColor(RiskStatus.Low),
                  getRiskColor(RiskStatus.Medium),
                  getRiskColor(RiskStatus.High),
                  getRiskColor(RiskStatus.Extreme),
                ],
              },
            },
            xAxis: {
              type: 'time',
              min: graphTicks[0],
              max: graphTicks[graphTicks.length - 1],
              axisLine: {
                show: false,
              },
              axisTick: {
                show: false,
                customValues: graphTicks,
              },
              axisLabel: {
                customValues: graphTicks,
                hideOverlap: true,
                show: true,
                showMinLabel: true,
                color: palette.text.secondary,
                fontFamily: typography.fontFamily,
                fontSize: 12,
                margin: 12,
                formatter: (value: number) => {
                  return ' ' + (formatValueToDate({ value: new Date(value) }, { month: 'short' }) ?? '') + ' '
                },
              },
              splitLine: {
                show: true,
                lineStyle: {
                  color: palette.grey[200],
                  type: 'solid',
                },
              },
            },
            yAxis: {
              type: 'value',
              min: 0,
              max: 3,
              axisLine: {
                show: false,
              },
              axisTick: {
                show: false,
              },
              axisLabel: {
                show: false,
              },
              splitLine: {
                show: false,
              },
            },
            series: [
              {
                type: 'line',
                animation: false,
                smooth: false,
                showSymbol: true,
                symbol: 'circle',
                symbolSize: 10,
                lineStyle: {
                  width: 2,
                },
                data: chartData.map(item => {
                  const color = getRiskColor(item.riskStatus)
                  return {
                    value: [item.timestamp, item.value],
                    itemStyle: {
                      color,
                    },
                    emphasis: {
                      itemStyle: {
                        color: getRiskStrongerColor(item.riskStatus),
                        borderWidth: 2,
                        borderColor: 'white',
                      },
                    },
                  }
                }),
                label: {
                  show: true,
                  position: 'top',
                  distance: 0,
                  formatter: params => {
                    const data = params.data as { value: [number, number] }
                    const [timestamp] = data.value
                    const item = timestampToItemMap.get(timestamp)
                    return formatMessage({
                      id: getRiskStatusShortKey(item?.riskStatus),
                    })
                  },
                  fontFamily: typography.fontFamily as string,
                  fontWeight: 600,
                  fontSize: 12,
                  backgroundColor: 'transparent',
                  padding: 6,
                  textBorderColor: 'white',
                  textBorderWidth: 6,
                  textShadowColor: 'white',
                  textShadowBlur: 6,
                },
                emphasis: {
                  scale: 1.4,
                  label: {
                    distance: -3,
                  },
                },
              },
            ],
          },
    [
      chartData,
      graphTicks,
      timestampToItemMap,
      palette.grey,
      palette.text.secondary,
      typography.fontFamily,
      formatMessage,
      formatValueToDate,
    ],
  )

  if (chartData.length === 0) {
    return (
      <EmptyState
        title={formatMessage({ id: 'general.noData' })}
        iconComponent={DoNotDisturbAltOutlined}
        variant={EmptyStateVariant.Small}
      />
    )
  }

  return <ReactEChartsCore echarts={echarts} option={option} style={{ height: 350, width: '100%' }} />
}

export default TopicalRiskProviderChart
